<script setup>
import { computed } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { rightDragWidth } = useWorkbenchLayout();
const { width: windowWidth } = useWindowSize();

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;

  if (isContactSidebarOpen) {
    return 0;
  }
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
    });
  }
};
</script>

<template>
  <div
    v-on-click-outside="[
      () => closeContactPanel(),
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="conv-sidebar-collapse bg-n-background h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-auto md:max-w-none ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="{
      'conv-sidebar-collapse--closed':
        activeTab !== 0 && rightDragWidth == null,
    }"
    :style="
      rightDragWidth != null ? { width: `${rightDragWidth}px` } : undefined
    "
  >
    <div
      class="conv-sidebar-collapse__inner"
      :class="
        rightDragWidth != null
          ? 'w-full min-w-0'
          : 'md:w-[320px] md:min-w-[320px] 2xl:w-[360px] 2xl:min-w-[360px]'
      "
    >
      <div class="flex flex-1 overflow-auto">
        <ContactPanel
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* SiteUp — colapso do painel de contato.
 *
 * A ANIMAÇÃO DE LARGURA FOI REMOVIDA (2026-08-10) depois de 3 tentativas e de
 * quebrar o fechamento em produção. O histórico, pra ninguém tentar de novo do
 * mesmo jeito:
 *
 *  1. `grid-template-columns: 1fr → 0fr` no elemento externo. NÃO fecha: o
 *     item de grid tinha `md:min-w-[320px]`, e min-width segura a track.
 *  2. Zerar o min-width do item. Min-width virou 0 (confirmado no computed) e
 *     a track continuou 320px: `0fr` distribui só o espaço LIVRE, e uma
 *     `width` DEFINIDA no item é base da track, não espaço livre.
 *  3. Mover a largura pro filho. Fechou, mas parou de ABRIR: sem largura no
 *     item, o container `w-auto` não tem o que distribuir e `1fr` resolve 0.
 *  4. `width: 0` no externo com transição. Ignorado: o elemento é item de FLEX
 *     e o pai o dimensiona pelo conteúdo.
 *
 * O que funciona e está medido no staging: `display: none` (fechado 0px,
 * aberto 321px). Correção > animação: um painel que não fecha é bug de
 * produto; a transição era enfeite.
 *
 * Se alguém quiser a animação de volta, o caminho provável é animar
 * `transform: translateX(100%)` com o painel em `position: absolute` — sem
 * participar do fluxo, nada disso acima se aplica. Precisa de mockup e QA
 * próprios.
 */
.conv-sidebar-collapse--closed {
  display: none;
}

.conv-sidebar-collapse__inner {
  display: flex;
  min-width: 0;
  overflow: hidden;
}
</style>
